/**
 * RockBand chart-overlay strategy (R119 rewrite).
 *
 *   1. Colour each candle by its chart-TF OverView trend ZONE (unchanged) — so
 *      the candle body reads as the regime: green = Strong/Probable Buy, grey =
 *      Neutral, orange/red = Probable/Strong Short.
 *
 *   2. Signals come from the chart-TF candle colour + the ALWAYS-HTF background
 *      zone (the mapped Higher Timeframe's OverView zone of the HTF bar that
 *      CONTAINS each candle's start time). This is independent of the chart's
 *      Bkground display setting. Mirrors backend
 *      rockBandRules.evaluateRockBandV2 verbatim so chart markers, the live scan
 *      and the backtest never diverge.
 *
 *      candle GREEN = chart-TF score ≥ strong;  candle RED = score ≤ −strong
 *      BUY   : GREEN candle + bg Green→High / bg Blue&HTF-up→Med / bg Black&HTF-up→Low
 *      SHORT : RED candle   + bg Red→High  / bg Orange&HTF-down→Med / bg Black&HTF-down→Low
 *      SELL  : RED candle while LONG with no qualifying SHORT  (exit long)
 *      COVER : GREEN candle while SHORT with no qualifying BUY (exit short)
 *
 * The cloud / Cond3 / split machinery of R114–R118 is fully removed (R119).
 * Feed CLOSED-bar inputs only (no repaint within already-closed HTF periods).
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, StrategyOverlayResult, ChartMarker, CandleColor, ChartStrategyOptions, TrendSeriesPoint } from './types';
import { OVERVIEW_ZONE_COLORS, zoneForScore, type OverviewZone } from '../../theme/overviewColors';

export const ROCKBAND_DEFAULT_THRESHOLDS = {
  strong: 60, probable: 20,
};

type Pos = 'LONG' | 'SHORT' | 'FLAT';

export type RockBandStrength = 'HIGH' | 'MEDIUM' | 'LOW' | null;
/** Display-facing signal vocabulary. EXIT_LONG = SELL, EXIT_SHORT = COVER. */
export type RbSignal = 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT' | null;

/** Bullish-ascending rank for the HTF "turned from" direction test. */
const ZONE_RANK: Record<OverviewZone, number> = {
  strongShort: 0, probableShort: 1, neutral: 2, probableBuy: 3, strongBuy: 4,
};

export interface RbV2Bar {
  time: number;
  cScore: number;      // chart-TF OverView score → candle colour
  hZone: OverviewZone; // HTF zone of the containing HTF bar (background)
}

export interface RbV2Step {
  time: number;
  signal: RbSignal;
  pos: Pos;
  strength: RockBandStrength;
  hZone: OverviewZone;
  candle: 'GREEN' | 'RED' | null;
}

/** Single source of truth for the R119 RockBand signals — mirrors
 *  backend/services/strategies/rockBandRules.evaluateRockBandV2 exactly. */
export function evaluateRockBandV2(bars: RbV2Bar[], strong = 60): RbV2Step[] {
  const out: RbV2Step[] = [];
  let pos: Pos = 'FLAT';
  const thr = Math.max(1, Math.min(100, strong));
  let curHtf: OverviewZone | null = null;
  let prevDistinctHtf: OverviewZone | null = null;

  for (const b of bars) {
    if (curHtf === null) curHtf = b.hZone;
    else if (b.hZone !== curHtf) { prevDistinctHtf = curHtf; curHtf = b.hZone; }
    const htfUp = prevDistinctHtf != null && ZONE_RANK[b.hZone] > ZONE_RANK[prevDistinctHtf];
    const htfDown = prevDistinctHtf != null && ZONE_RANK[b.hZone] < ZONE_RANK[prevDistinctHtf];

    const candleGreen = b.cScore >= thr;
    const candleRed = b.cScore <= -thr;

    let buyStrength: RockBandStrength = null;
    if (candleGreen) {
      if (b.hZone === 'strongBuy') buyStrength = 'HIGH';
      else if (b.hZone === 'probableBuy' && htfUp) buyStrength = 'MEDIUM';
      else if (b.hZone === 'neutral' && htfUp) buyStrength = 'LOW';
    }
    let shortStrength: RockBandStrength = null;
    if (candleRed) {
      if (b.hZone === 'strongShort') shortStrength = 'HIGH';
      else if (b.hZone === 'probableShort' && htfDown) shortStrength = 'MEDIUM';
      else if (b.hZone === 'neutral' && htfDown) shortStrength = 'LOW';
    }

    let signal: RbSignal = null;
    let strength: RockBandStrength = null;
    if (pos === 'LONG') {
      if (candleRed) {
        if (shortStrength) { signal = 'SHORT'; strength = shortStrength; pos = 'SHORT'; }
        else { signal = 'EXIT_LONG'; pos = 'FLAT'; }
      }
    } else if (pos === 'SHORT') {
      if (candleGreen) {
        if (buyStrength) { signal = 'BUY'; strength = buyStrength; pos = 'LONG'; }
        else { signal = 'EXIT_SHORT'; pos = 'FLAT'; }
      }
    } else {
      if (candleGreen && buyStrength) { signal = 'BUY'; strength = buyStrength; pos = 'LONG'; }
      else if (candleRed && shortStrength) { signal = 'SHORT'; strength = shortStrength; pos = 'SHORT'; }
    }

    out.push({
      time: b.time, signal, pos, strength, hZone: b.hZone,
      candle: candleGreen ? 'GREEN' : candleRed ? 'RED' : null,
    });
  }
  return out;
}

export function computeRockBandOverlay(
  candles: CandleRow[],
  tfSeries: TrendSeriesPoint[],
  htfSeries: TrendSeriesPoint[],
  thresholds: Partial<typeof ROCKBAND_DEFAULT_THRESHOLDS> = {},
): StrategyOverlayResult {
  const th = { ...ROCKBAND_DEFAULT_THRESHOLDS, ...thresholds };
  if (!candles?.length || !tfSeries?.length) return { candleColors: [], markers: [] };

  const tfByTime = new Map<number, number>();
  for (const p of tfSeries) tfByTime.set(p.time, p.score);

  // HTF zone per candle — forward-fill by START time (the HTF bar containing
  // this candle's start time), matching the R119 Bkground HTF method. Always
  // HTF; independent of the chart's Bkground display setting.
  const htfSorted = (htfSeries || []).slice().sort((a, b) => a.time - b.time);
  const hZoneByTime = new Map<number, OverviewZone>();
  if (htfSorted.length) {
    let cur = -1;
    for (const c of candles) {
      const t = c.time as number;
      while (cur + 1 < htfSorted.length && htfSorted[cur + 1].time <= t) cur++;
      if (cur >= 0 && Number.isFinite(htfSorted[cur].score)) {
        hZoneByTime.set(t, zoneForScore(htfSorted[cur].score, th.strong, th.probable));
      }
    }
  }

  // 1. Per-bar candle zone colour (regime read) + the evaluator's input bar.
  const candleColors: CandleColor[] = [];
  const bars: RbV2Bar[] = [];
  for (const c of candles) {
    const tf = tfByTime.get(c.time);
    if (tf == null) {
      // No OverView score for this bar (warmup, or the only ON constituent is
      // 'na' here — e.g. a latched voter before its first signal). Paint it
      // NEUTRAL grey rather than letting it fall back to the raw up/down price
      // colour, so isolating a single constituent gives a clean, unambiguous
      // read (its up/down where it has a signal, grey where it doesn't).
      candleColors.push({ time: c.time, upColor: OVERVIEW_ZONE_COLORS.neutral, downColor: OVERVIEW_ZONE_COLORS.neutral });
      continue;
    }
    const zoneColor = OVERVIEW_ZONE_COLORS[zoneForScore(tf, th.strong, th.probable)];
    candleColors.push({ time: c.time, upColor: zoneColor, downColor: zoneColor });
    const hZone = hZoneByTime.get(c.time);
    if (hZone == null) continue; // no HTF background yet → bar can't signal
    bars.push({ time: c.time, cScore: tf, hZone });
  }

  // 2. Walk the evaluator → entry / exit markers + per-bar tooltip data.
  const steps = evaluateRockBandV2(bars, th.strong);
  const markers: ChartMarker[] = [];
  const signalsByTime = new Map<number, { signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT'; strength: RockBandStrength; pos: Pos }>();
  const positionByTime = new Map<number, Pos>();
  for (const s of steps) {
    positionByTime.set(s.time, s.pos);
    if (!s.signal) continue;
    signalsByTime.set(s.time, { signal: s.signal, strength: s.strength, pos: s.pos });
    if (s.signal === 'BUY')        markers.push({ time: s.time, position: 'belowBar', text: 'B', color: '#00C853', shape: 'arrowUp',   size: 2 });
    else if (s.signal === 'SHORT') markers.push({ time: s.time, position: 'aboveBar', text: 'S', color: '#D50000', shape: 'arrowDown', size: 2 });
    else if (s.signal === 'EXIT_LONG')  markers.push({ time: s.time, position: 'aboveBar', text: 'X', color: '#ff9800', shape: 'circle' });
    else if (s.signal === 'EXIT_SHORT') markers.push({ time: s.time, position: 'belowBar', text: 'X', color: '#ff9800', shape: 'circle' });
  }
  return { candleColors, markers, signalsByTime, positionByTime };
}

export const rockBandStrategy: ChartStrategy = {
  id: 'rockband',
  name: 'RockBand',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    if (!opts?.tfTrendSeries?.length) return { candleColors: [], markers: [] };
    return computeRockBandOverlay(
      candles,
      opts.tfTrendSeries,
      opts.htfTrendSeries || [],
      opts.rockBandThresholds,
    );
  },
};
